<template>
  <div>
    <template v-if="!print_mode">
      <div class="d-flex flex-column w-100">
        <h5 v-if="hasTitle && show_title" class="mb-3 fw-600 gray-800" :style="titleStyle">
          <span v-if="lang === 'KOR'">
            {{ disclosureInfo.image_title }}
          </span>
          <span v-else>
            {{ disclosureInfo.image_title_em || disclosureInfo.image_title_ea }}
          </span>
        </h5>

        <div
          align="center"
          class="d-flex mb-2 text-center"
          :class="imageAlignmentClass"
          :style="{ overflow: 'visible' }"
          @click="openImgPopup"
        >
          <b-img
            v-if="lang === 'KOR'"
            :style="userImageWidthVar"
            class="preview-image border-radius cursor"
            :src="imageUrl"
            @load="onImageLoad"
          />
          <b-img
            v-else
            :style="userImageWidthVar"
            class="preview-image border-radius cursor"
            :src="imageUrlE"
            @load="onImageLoad"
          />
        </div>

        <div>
          <p class="fw-400 f-90" :style="[captionStyle, { whiteSpace: 'pre-line' }]">
            <span v-if="content_caption !== 'null'">
              {{ content_caption }}
            </span>
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <b-row>
        <b-col v-if="show_title" cols="2">
          <h6 v-if="hasTitle" class="mb-3 fw-600 gray-800 border-top-dark pt-2">
            <span v-if="lang === 'KOR'">
              {{ disclosureInfo.image_title }}
            </span>
            <span v-else>
              {{ disclosureInfo.image_title_em || disclosureInfo.image_title_ea }}
            </span>
          </h6>
        </b-col>
        <b-col cols="10" :offset="show_title ? 0 : 2">
          <div
            align="center"
            class="d-flex mb-2 text-center"
            :class="imageAlignmentClass"
            :style="{ overflow: 'visible' }"
            @click="openImgPopup"
          >
            <b-img v-if="lang === 'KOR'" style="width: 100%" :src="imageUrl" @load="onImageLoad" />
            <b-img v-else style="width: 100%" :src="imageUrlE" @load="onImageLoad" />
          </div>

          <div v-if="hasCaption">
            <p class="fw-400 f-90" style="white-space: pre-line">
              <span v-if="lang === 'KOR'">
                {{ disclosureInfo.image_caption }}
              </span>
              <span v-else>
                {{ disclosureInfo.image_caption_em || disclosureInfo.image_caption_ea }}
              </span>
            </p>
          </div>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ImageContent',
  props: {
    disclosureInfo: { type: Object, required: true },
    lang: { type: String, required: true },
    print_mode: { type: Boolean, default: false },
    show_title: { type: Boolean, default: true }
  },
  computed: {
    hasTitle() {
      return (
        this.disclosureInfo.image_title &&
        this.disclosureInfo.image_title !== 'null' &&
        this.disclosureInfo.image_title !== ' '
      );
    },
    content_caption() {
      return this.lang !== 'ENG'
        ? this.disclosureInfo.image_caption
        : this.disclosureInfo.image_caption_em
        ? this.disclosureInfo.image_caption_em
        : this.disclosureInfo.image_caption_ea;
    },
    hasCaption() {
      return (
        this.disclosureInfo.image_caption &&
        this.disclosureInfo.image_caption !== 'null' &&
        this.disclosureInfo.image_caption !== ' '
      );
    },
    imageAlignmentClass() {
      const alignment = this.disclosureInfo.alignment;
      if (alignment === 'left') {
        return 'justify-content-start';
      } else if (alignment === 'right') {
        return 'justify-content-end';
      } else {
        return 'justify-content-center';
      }
    },
    imageUrl() {
      return this.disclosureInfo.image_url
        ? this.$store.state.static_host + this.disclosureInfo.image_url.replace(/\\/g, '/')
        : null;
    },
    imageUrlE() {
      return this.disclosureInfo.image_url_e
        ? this.$store.state.static_host + this.disclosureInfo.image_url_e.replace(/\\/g, '/')
        : null;
    },
    userImageWidthVar() {
      // 사용자 설정 image_width(%)을 CSS 변수로 노출 → .preview-image의 width 규칙이 참조,
      // 모바일에서는 미디어 쿼리로 100%로 오버라이드 가능 (#663)
      return { '--user-image-width': (this.disclosureInfo.image_width || 100) + '%' };
    },
    imageBoundaryOffsets() {
      const width = this.disclosureInfo.image_width || 100;
      let leftOffset, rightOffset;
      const alignment = this.disclosureInfo.alignment;
      if (alignment === 'left') {
        leftOffset = 0;
        rightOffset = width;
      } else if (alignment === 'center') {
        leftOffset = (100 - width) / 2;
        rightOffset = (100 - width) / 2 + width;
      } else {
        leftOffset = 100 - width;
        rightOffset = 100;
      }
      return { leftOffset: `${leftOffset}%`, rightOffset: `${rightOffset}%` };
    },
    titleStyle() {
      switch (this.disclosureInfo.title_alignment) {
        case 'text-start':
          return { textAlign: 'left' };
        case 'center':
          return { textAlign: 'center' };
        case 'text-end':
          return { textAlign: 'right' };
        case 'image-left':
          return {
            textAlign: 'left',
            marginLeft: this.imageBoundaryOffsets.leftOffset,
            marginRight: this.imageBoundaryOffsets.leftOffset
          };
        case 'image-right':
          return {
            textAlign: 'right',
            marginLeft: this.imageBoundaryOffsets.leftOffset,
            marginRight: this.imageBoundaryOffsets.leftOffset
          };
        default:
          return {};
      }
    },
    captionStyle() {
      switch (this.disclosureInfo.caption_alignment) {
        case 'text-start':
          return { textAlign: 'left' };
        case 'center':
          return { textAlign: 'center' };
        case 'text-end':
          return { textAlign: 'right' };
        case 'image-left':
          return {
            textAlign: 'left',
            marginLeft: this.imageBoundaryOffsets.leftOffset,
            marginRight: this.imageBoundaryOffsets.leftOffset
          };
        case 'image-right':
          return {
            textAlign: 'right',
            marginLeft: this.imageBoundaryOffsets.leftOffset,
            marginRight: this.imageBoundaryOffsets.leftOffset
          };
        default:
          return {};
      }
    }
  },
  methods: {
    openImgPopup(e) {
      console.log(e);
      const img = e.target;
      if (img.tagName === 'IMG') {
        this.$EventBus.$emit('image-modal', img.getAttribute('src'), true);
      }
    },
    onImageLoad() {
      this.$emit('image-loaded');
    }
  }
};
</script>

<style scoped>
h5,
p {
  font-family: inherit;
  line-height: 1.6;
  font-size: 1rem;
  font-weight: 400;
  letter-spacing: -0.01px;
  color: #111111;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

h5 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #343434;
  margin-bottom: 0.5rem;
}

strong {
  font-weight: 600;
}

/* #663 반응형 이미지: 사용자 image_width를 base로 두되, 부모 폭을 넘지 않도록 보장.
 * 모바일(<576px)에서는 사용자 설정과 무관하게 100%로 강제 — 작은 화면에서 50% 등
 * 사용자 설정값이 지나치게 작아 보이는 문제 해결.
 * --user-image-width: 컴포넌트가 inline style로 노출 (기본 100%). */
.preview-image {
  width: var(--user-image-width, 100%);
  max-width: var(--preview-img-max-width, 100%);
  height: auto;
}

@media screen and (max-width: 575.98px) {
  .preview-image {
    width: 100%;
  }
}

/* 이미지 품질 개선 - 부드러운 렌더링 - 이 컴포넌트에서만 적용 */
img {
  /* 고품질 스무딩 활성화 */
  image-rendering: auto;
  image-rendering: -webkit-optimize-contrast;

  /* 브라우저 기본 안티앨리어싱 유지 */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  /* 하드웨어 가속으로 렌더링 품질 향상 */
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  backface-visibility: hidden;

  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

/* Bootstrap Vue 컴포넌트 내부 img 태그 타겟팅 - 이 컴포넌트에서만 적용 */
::v-deep .b-img img {
  image-rendering: auto !important;
  image-rendering: -webkit-optimize-contrast !important;

  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;

  -webkit-backface-visibility: hidden !important;
  -moz-backface-visibility: hidden !important;
  backface-visibility: hidden !important;

  transform: translateZ(0) !important;
  -webkit-transform: translateZ(0) !important;
}
</style>
